import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApiConfiguration } from 'nekst-api';
import { useEventsPublisher } from 'event-bus';
import { ChatWidgetContextProvider } from './ChatWidgetContext';
import { peekTransferState } from './chatTransferStore';
import { ChatWidgetErrorBoundary } from './ChatWidgetErrorBoundary';
import ChatFab from './Layout/Components/ChatFab';
import ChatWidgetLayout from './Layout';

export const CHAT_WIDGET_OPENED = 'CHAT_WIDGET_OPENED';
export const CHAT_WIDGET_CLOSED = 'CHAT_WIDGET_CLOSED';

function ChatWidgetInner() {
  const initialTransfer = useRef(peekTransferState());
  const [isOpen, setIsOpen] = useState(() => initialTransfer.current !== null);
  const navigate = useNavigate();
  const eventsPublisher = useEventsPublisher();

  const resumedConversationId = initialTransfer.current?.conversationId;

  useEffect(() => {
    eventsPublisher.publish(isOpen ? CHAT_WIDGET_OPENED : CHAT_WIDGET_CLOSED, {});
    return () => {
      eventsPublisher.publish(CHAT_WIDGET_CLOSED, {});
    };
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleExpand = useCallback((conversationId: number | null) => {
    if (conversationId) {
      navigate(`/chats/${conversationId}`);
    } else {
      navigate('/chats');
    }
  }, [navigate]);

  return (
    <ChatWidgetContextProvider conversationId={resumedConversationId}>
      {isOpen ? (
        <ChatWidgetLayout
          onClose={handleClose}
          onExpand={handleExpand}
        />
      ) : (
        <ChatFab onClick={handleOpen} />
      )}
    </ChatWidgetContextProvider>
  );
}

export function ChatWidget() {
  const { streamingGatewayUrl } = useApiConfiguration();
  const { pathname } = useLocation();

  if (!streamingGatewayUrl || pathname.startsWith('/chats')) {
    return null;
  }

  return (
    <ChatWidgetErrorBoundary>
      <ChatWidgetInner />
    </ChatWidgetErrorBoundary>
  );
}
